import type { PendingUpload, PullResult, PushResult, Transport } from '@machs/shared';

/**
 * Die Anmeldung liegt im Web in einem HttpOnly-Cookie, das der Server setzt:
 * dort kommt kein Skript im Ursprung heran. Im Browser-Speicher steht nur der
 * Hinweis, dass eine Anmeldung besteht — kein Geheimnis. `TOKEN_KEY` ist der
 * alte Ablageort und wird beim Start noch einmal übernommen.
 */
const TOKEN_KEY = 'machs.token';
const SIGNED_IN_KEY = 'machs.angemeldet';
const SIGN_OUT_KEY = 'machs.abmelden';

const read = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const write = (key: string, value: string | null) => {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    // Privater Modus: gilt nur für diese Sitzung
  }
};

// Gast-Ansicht: eigenes Token je Freigabe-Link, getrennt von einer Team-Anmeldung
let guestToken: string | null = null;

export function setGuestToken(token: string | null) {
  guestToken = token;
  clearUrlToken();
}

/** Besteht eine Anmeldung? Das Token selbst ist für den Client unlesbar. */
export function hasSession(): boolean {
  return read(SIGNED_IN_KEY) === '1' || !!read(TOKEN_KEY);
}

function markSignedIn() {
  write(SIGNED_IN_KEY, '1');
  write(TOKEN_KEY, null);
  clearUrlToken();
}

/**
 * Nach einer Anmeldung: der Server hat das Cookie in der Antwort gesetzt, hier
 * wird nur der Hinweis gesetzt. Beim Anmelde-Link (`#token=`) und bei einer
 * Sitzung aus dem alten Ablageort muss das Token dem Server erst gezeigt
 * werden, damit er daraus ein Cookie macht.
 */
async function adoptToken(token: string): Promise<boolean> {
  write(SIGN_OUT_KEY, null);
  try {
    await api('/auth/session', { method: 'POST', headers: { authorization: `Bearer ${token}` } });
    markSignedIn();
    return true;
  } catch {
    write(TOKEN_KEY, null);
    return false;
  }
}

export function sessionStarted() {
  markSignedIn();
}

/**
 * Beendet die Anmeldung. Löschen kann nur der Server, das Cookie ist für den
 * Client unsichtbar — scheitert der Aufruf (kein Netz), bleibt ein Vermerk und
 * der nächste Start holt es nach.
 */
export async function signOut(): Promise<void> {
  write(SIGNED_IN_KEY, null);
  write(TOKEN_KEY, null);
  write(SIGN_OUT_KEY, '1');
  clearUrlToken();
  try {
    await api('/auth/logout', { method: 'POST' });
    write(SIGN_OUT_KEY, null);
  } catch {
    // Beim nächsten Start erneut versuchen
  }
}

/**
 * Beim Start: ein offenes Abmelden nachholen, danach ein Token aus dem
 * Anmelde-Link oder aus dem alten Ablageort (vor der Umstellung lag es im
 * Browser-Speicher) in ein Cookie überführen.
 */
export async function prepareSession(linkToken: string | null): Promise<void> {
  if (read(SIGN_OUT_KEY)) {
    await api('/auth/logout', { method: 'POST' })
      .then(() => write(SIGN_OUT_KEY, null))
      .catch(() => undefined);
  }
  const token = linkToken ?? read(TOKEN_KEY);
  if (token) await adoptToken(token);
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
  ) {
    super(message);
  }
}

export async function api<T>(path: string, init: RequestInit & { json?: unknown } = {}): Promise<T> {
  const headers = new Headers(init.headers);
  // Die Gast-Ansicht bringt ihren Zugang selbst mit; die Anmeldung eines Kontos
  // reist als Cookie. Das Kennzeichen weist die Anfrage als eigene aus, ein
  // fremdes Formular kann es nicht setzen.
  if (guestToken && !headers.has('authorization')) headers.set('authorization', `Bearer ${guestToken}`);
  headers.set('x-machs-web', '1');
  if (init.json !== undefined) headers.set('content-type', 'application/json');
  let res: Response;
  try {
    res = await fetch(`/api${path}`, { ...init, headers, body: init.json !== undefined ? JSON.stringify(init.json) : init.body });
  } catch {
    throw new ApiError('Keine Verbindung zum Server', 0);
  }
  const body = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok) {
    if (res.status === 401) window.dispatchEvent(new Event(guestToken ? 'machs:guest-invalid' : 'machs:logout'));
    throw new ApiError(body.error ?? `Fehler ${res.status}`, res.status);
  }
  return body;
}

/**
 * Bilder, Videos und `EventSource` können keinen `Authorization`-Header setzen.
 * Das Anmelde-Token darf dafür nicht in die Adresse wandern, dort landet es in
 * Zugriffslogs, Zwischenspeichern und im Verlauf. Stattdessen holt der Client
 * ein kurzlebiges Lese-Token, das der Server als HttpOnly-Cookie setzt; die
 * Adressen unten brauchen deshalb keinen Zugang mehr im Klartext.
 */
let urlToken: { value: string; expiresAt: number } | null = null;
let pending: Promise<void> | null = null;

export async function refreshUrlToken(): Promise<void> {
  // Nur die Gast-Ansicht braucht es; ein angemeldetes Konto lädt Medien über
  // das Anmelde-Cookie
  if (!guestToken) return;
  pending ??= api<{ token: string; expiresIn: number }>('/auth/url-token', { method: 'POST' })
    .then((r) => {
      urlToken = { value: r.token, expiresAt: Date.now() + r.expiresIn * 1000 };
    })
    .catch(() => {
      urlToken = null;
    })
    .finally(() => {
      pending = null;
    });
  return pending;
}

export function clearUrlToken() {
  urlToken = null;
}

/**
 * Die Gast-Ansicht eines Freigabe-Links kann neben einer Team-Anmeldung im
 * selben Browser laufen; beide würden sich dasselbe Cookie überschreiben.
 * Deshalb reist dort das (ebenso kurzlebige, nur lesende) Token in der Adresse.
 */
function urlTokenParam(): string {
  if (!guestToken) return '';
  if (urlToken && urlToken.expiresAt <= Date.now()) urlToken = null;
  return `access_token=${encodeURIComponent(urlToken?.value ?? '')}`;
}

/** Erneuert das Lese-Cookie, sobald es zur Neige geht (Aufruf bei Sichtwechsel). */
export function ensureUrlToken(): void {
  if ((urlToken?.expiresAt ?? 0) - Date.now() < 15 * 60_000) void refreshUrlToken();
}

/** Adresse der Live-Hinweise (SSE), die ebenfalls keinen Header setzen kann. */
export function eventsUrl(): string {
  const param = urlTokenParam();
  return param ? `/api/events?${param}` : '/api/events';
}

export function mediaUrl(mediaId: string): string {
  const param = urlTokenParam();
  return `/api/media/${mediaId}/file${param ? `?${param}` : ''}`;
}

/** Standbild eines Videos (Vorschau neben dem Video). */
export function mediaFrameUrl(mediaId: string, nr: number): string {
  const param = urlTokenParam();
  return `/api/media/${mediaId}/frame/${nr}${param ? `?${param}` : ''}`;
}

/** Vorschaubild eines Links (vom Server gespeichert). */
export function mediaPreviewUrl(mediaId: string, updatedAt: string): string {
  const param = urlTokenParam();
  // Zeitstempel im Pfad, damit ein nachgeladenes Bild den alten Zwischenspeicher umgeht
  return `/api/media/${mediaId}/preview?v=${encodeURIComponent(updatedAt)}${param ? `&${param}` : ''}`;
}

export const transport: Transport = {
  push: (body) => api<PushResult>('/sync/push', { method: 'POST', json: body }),
  pull: (cursor) => api<PullResult>(`/sync/pull?cursor=${cursor}`),
  async upload(u: PendingUpload) {
    if (u.kind === 'link') {
      await api('/media/link', { method: 'POST', json: { id: u.mediaId, url: u.url } });
      return;
    }
    const form = new FormData();
    form.set('id', u.mediaId);
    form.set('kind', u.kind);
    form.set('file', u.file as Blob, u.fileName ?? 'datei');
    if (u.durationSec) form.set('durationSec', String(u.durationSec));
    if (u.width) form.set('width', String(u.width));
    if (u.height) form.set('height', String(u.height));
    await api('/media', { method: 'POST', body: form });
  },
};

export function userAvatarUrl(userId: string, avatarAt: string | null): string | null {
  if (!avatarAt) return null;
  const param = urlTokenParam();
  return `/api/users/${userId}/avatar?v=${encodeURIComponent(avatarAt)}${param ? `&${param}` : ''}`;
}

export interface Me {
  branding?: { name: string; color: string; logo: string | null } | null;
  user: { id: string; email: string; name: string; avatarAt: string | null };
  role: 'owner' | 'admin' | 'mitglied';
  tenant: { id: string; name: string; plan: string };
  teams: { id: string; name: string; role: string }[];
  members: { userId: string; name: string; email: string; role: string; avatarAt: string | null }[];
  /** Nur in der Gast-Ansicht (Zugriff über einen Freigabe-Link) */
  guest?: GuestInfo;
}

export interface GuestInfo {
  shareId: string;
  guestId: string;
  taskId: string | null;
  projectId: string | null;
  canWrite: boolean;
  expiresAt: string | null;
}

/** Freigabe aus Sicht des Teams */
export interface Share {
  id: string;
  taskId: string | null;
  projectId: string | null;
  label: string | null;
  canWrite: boolean;
  expiresAt: string | null;
  createdBy: string;
  createdAt: string;
  lastUsedAt: string | null;
  active: boolean;
  url: string | null;
  guests: { id: string; name: string; joinedAt: string; lastSeenAt: string | null }[];
}
